<template>
  <div class="kb-chat">
    <!-- Hidden while a drawer is open: it would sit on top of the drawer's
         controls (the drawer covers the right edge where the toggle lives).
         Below lg the top bar has the chat button instead. -->
    <button
      v-if="!isOpen && !isDrawerOpen"
      type="button"
      class="kb-chat-toggle"
      aria-label="Open chat assistant"
      @click="toggleChat"
    >
      <i
        class="pi pi-comments"
        aria-hidden="true"
      />
    </button>

    <!-- Docked on the right; a bottom sheet on a phone -->
    <Transition name="kb-chat-slide">
      <aside
        v-if="isOpen"
        class="kb-chat-panel"
        aria-labelledby="kb-chat-title"
        @keydown.esc="closeChat"
      >
        <div
          class="kb-chat-resize"
          aria-hidden="true"
          @mousedown="startResize"
        />

        <header class="kb-chat-head">
          <h2
            id="kb-chat-title"
            class="kb-chat-title"
          >
            <i
              class="pi pi-android text-kb-accent"
              aria-hidden="true"
            />
            Deep Thought
          </h2>
          <div class="flex gap-1">
            <Button
              v-tooltip.left="'Clear chat'"
              icon="pi pi-trash"
              severity="secondary"
              text
              size="small"
              aria-label="Clear chat history"
              @click="clearChat"
            />
            <Button
              icon="pi pi-times"
              severity="secondary"
              text
              size="small"
              aria-label="Close chat"
              @click="closeChat"
            />
          </div>
        </header>

        <ChatbotMessages />
        <ChatbotInput />
      </aside>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import Button from 'primevue/button'

const { isOpen, toggleChat, closeChat, clearChat } = useChatbot()
const { isDrawerOpen } = useDrawer()

const panelWidth = ref(400)
const isResizing = ref(false)
const MIN_WIDTH = 320
const MAX_WIDTH = 800

function startResize(e: MouseEvent) {
  isResizing.value = true
  document.addEventListener('mousemove', onResize)
  document.addEventListener('mouseup', stopResize)
  e.preventDefault()
}

function onResize(e: MouseEvent) {
  if (!isResizing.value) return
  const newWidth = window.innerWidth - e.clientX
  panelWidth.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth))
}

function stopResize() {
  isResizing.value = false
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
  // Save preference
  try {
    localStorage.setItem('chatbot-width', String(panelWidth.value))
  }
  catch {
    // Storage blocked (private mode): the width just isn't remembered
  }
}

onMounted(() => {
  let saved: string | null = null
  try {
    saved = localStorage.getItem('chatbot-width')
  }
  catch {
    saved = null
  }
  if (saved) {
    panelWidth.value = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, parseInt(saved)))
  }
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onResize)
  document.removeEventListener('mouseup', stopResize)
})
</script>

<style scoped>
.kb-chat-toggle {
  position: fixed;
  right: 24px;
  bottom: 44px;
  z-index: 1000;
  display: none;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border: 0;
  border-radius: 50%;
  background: var(--kb-accent);
  color: var(--kb-accent-ink);
  box-shadow: 0 8px 24px var(--kb-shadow);
  cursor: pointer;
}

.kb-chat-toggle .pi {
  font-size: 1.25rem;
}

.kb-chat-toggle:focus-visible {
  outline: 2px solid var(--kb-fg);
  outline-offset: 3px;
}

@media (min-width: 1024px) {
  .kb-chat-toggle {
    display: flex;
  }
}

/* The panel: a pane down the right edge, sized by the handle on its left */
.kb-chat-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  width: v-bind('panelWidth + "px"');
  max-width: 90vw;
  overflow: hidden;
  border-left: 1px solid var(--kb-line);
  background: var(--kb-pane);
  box-shadow: -12px 0 32px var(--kb-shadow);
}

.kb-chat-resize {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  width: 6px;
  cursor: ew-resize;
}

.kb-chat-resize:hover,
.kb-chat-resize:active {
  background: var(--kb-accent);
}

.kb-chat-head {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-height: 52px;
  padding: 0.25rem 0.5rem 0.25rem 1rem;
  border-bottom: 1px solid var(--kb-line);
}

.kb-chat-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--kb-fg);
}

.kb-chat-slide-enter-active,
.kb-chat-slide-leave-active {
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s;
}

.kb-chat-slide-enter-from,
.kb-chat-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .kb-chat-slide-enter-active,
  .kb-chat-slide-leave-active {
    transition: none;
  }
}

/* A phone: a bottom sheet over the tab bar */
@media (max-width: 480px) {
  .kb-chat-panel {
    top: auto;
    left: 0;
    width: 100%;
    max-width: 100%;
    height: 80dvh;
    border-top: 1px solid var(--kb-line);
    border-left: 0;
    border-radius: 4px 4px 0 0;
    box-shadow: 0 -12px 32px var(--kb-shadow);
  }

  .kb-chat-resize {
    display: none;
  }

  .kb-chat-slide-enter-from,
  .kb-chat-slide-leave-to {
    transform: translateY(100%);
  }
}
</style>
